const homeSliderEditorState = new Map();

class HomeSliderComponent extends HTMLElement {
  connectedCallback() {
    this.initWhenReady();
  }

  disconnectedCallback() {
    this.destroySwiper();
  }

  isAutoHeight() {
    return this.dataset.slideHeight === 'auto' || this.classList.contains('slideshow--auto');
  }

  getSectionKey() {
    return this.closest('[id^="shopify-section-"]')?.id || '';
  }

  initWhenReady() {
    if (typeof Swiper === 'undefined') {
      window.setTimeout(() => {
        if (this.isConnected) this.initWhenReady();
      }, 50);
      return;
    }
    this.initSwiper();
  }

  destroySwiper() {
    this.teardownAutoHeight();
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  isDesignMode() {
    return Boolean(window.Shopify?.designMode);
  }

  getOriginalSlides() {
    return Array.from(
      this.querySelectorAll('.swiper-wrapper > .swiper-slide:not(.swiper-slide-duplicate)')
    );
  }

  slideIndexFor(blockEl) {
    const slide = blockEl?.classList?.contains('swiper-slide')
      ? blockEl
      : blockEl?.closest?.('.swiper-slide');
    if (!slide || !this.contains(slide)) return -1;

    const loopIndex = parseInt(slide.getAttribute('data-swiper-slide-index'), 10);
    if (!Number.isNaN(loopIndex)) return loopIndex;

    return this.getOriginalSlides().indexOf(slide);
  }

  rememberSlideIndex(index) {
    const key = this.getSectionKey();
    if (key && index >= 0) homeSliderEditorState.set(key, index);
  }

  selectSlide(blockEl, detail = {}) {
    const goToSlide = (tries = 0) => {
      if (!this.swiper) {
        if (tries < 20) window.setTimeout(() => goToSlide(tries + 1), 50);
        return;
      }

      const index = this.slideIndexFor(blockEl);
      if (index < 0) return;

      this.rememberSlideIndex(index);
      this.swiper.autoplay?.stop();

      const speed = detail.load ? 0 : 400;
      if (this.swiper.params.loop && typeof this.swiper.slideToLoop === 'function') {
        this.swiper.slideToLoop(index, speed);
      } else {
        this.swiper.slideTo(index, speed);
      }
    };

    goToSlide();
  }

  restoreEditorSlide() {
    if (!this.isDesignMode() || !this.swiper) return;
    const index = homeSliderEditorState.get(this.getSectionKey());
    if (!Number.isInteger(index) || index < 0) return;
    this.swiper.autoplay?.stop();
    if (this.swiper.params.loop && typeof this.swiper.slideToLoop === 'function') {
      this.swiper.slideToLoop(index, 0);
    } else {
      this.swiper.slideTo(index, 0);
    }
  }

  deselectSlide() {
    const container = this.querySelector('.home-slider-swiper');
    if (container?.dataset.autoplay === 'true' && this.swiper?.autoplay) {
      this.swiper.autoplay.start();
    }
  }

  initSwiper() {
    const container = this.querySelector('.home-slider-swiper');
    if (!container) return;

    const slideCount = container.querySelectorAll('.swiper-slide').length;
    if (slideCount < 1) return;

    const autoplayEnabled = container.dataset.autoplay === 'true' && slideCount > 1;
    const delay = (parseInt(container.dataset.speed, 10) || 5) * 1000;
    const showDots = container.dataset.showDots === 'true' && slideCount > 1;
    const arrowsWrap = this.querySelector('.slideshow__arrows');
    const prevEl = this.querySelector('.slideshow__arrow--prev');
    const nextEl = this.querySelector('.slideshow__arrow--next');
    const paginationEl = this.querySelector('.slideshow__dots');
    const autoplayBtn = this.querySelector('.slideshow__autoplay');
    const arrowsEnabled = Boolean(
      prevEl && nextEl && slideCount > 1 && arrowsWrap && !arrowsWrap.hasAttribute('hidden')
    );

    this.destroySwiper();

    const options = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: slideCount > 1 && !this.isDesignMode(),
      speed: 700,
      grabCursor: slideCount > 1,
      watchOverflow: true,
      effect: 'slide',
      a11y: {
        enabled: true,
      },
      on: {
        init: (swiper) => {
          this.updateSlideA11y(swiper);
        },
        slideChange: (swiper) => {
          this.updateSlideA11y(swiper);
        },
      },
    };

    if (arrowsEnabled) {
      options.navigation = {
        prevEl,
        nextEl,
      };
    }

    if (showDots && paginationEl) {
      options.pagination = {
        el: paginationEl,
        clickable: true,
        bulletClass: 'slider-counter__link',
        bulletActiveClass: 'slider-counter__link--active',
        renderBullet(index, className) {
          return (
            `<button type="button" class="${className}" aria-label="Go to slide ${index + 1}">` +
            `<span class="slideshow__dot" aria-hidden="true"></span>` +
            `</button>`
          );
        },
      };
    }

    if (autoplayEnabled) {
      options.autoplay = {
        delay,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,
      };
      options.on.autoplayTimeLeft = (_swiper, _time, progress) => {
        if (!autoplayBtn) return;
        const pct = Math.max(0, Math.min(100, (1 - progress) * 100));
        autoplayBtn.style.setProperty('--autoplay-progress', `${pct}%`);
      };
    }

    this.swiper = new Swiper(container, options);
    this.bindAutoplayButton(autoplayBtn, autoplayEnabled);
    this.restoreEditorSlide();
    this.setupAutoHeight();
  }

  setupAutoHeight() {
    this.teardownAutoHeight();
    if (!this.isAutoHeight()) return;

    this.equalizeSlideHeights();

    this._onAutoHeightResize = () => {
      const width = Math.round(this.getBoundingClientRect().width);
      if (width === this._autoHeightWidth) return;
      this.equalizeSlideHeights();
    };

    if (typeof ResizeObserver === 'function') {
      this._autoHeightObserver = new ResizeObserver(this._onAutoHeightResize);
      this._autoHeightObserver.observe(this);
    } else {
      window.addEventListener('resize', this._onAutoHeightResize);
    }

    this._onAutoHeightMedia = () => this.equalizeSlideHeights();
    this.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', this._onAutoHeightMedia, { once: true });
    });
    this.querySelectorAll('video').forEach((video) => {
      if (video.readyState < 1) {
        video.addEventListener('loadedmetadata', this._onAutoHeightMedia, { once: true });
      }
    });
  }

  teardownAutoHeight() {
    this._autoHeightObserver?.disconnect();
    this._autoHeightObserver = null;

    if (this._onAutoHeightResize) {
      window.removeEventListener('resize', this._onAutoHeightResize);
    }

    this._onAutoHeightResize = null;
    this._onAutoHeightMedia = null;
    this._autoHeightWidth = null;
    this.classList.remove('slideshow--auto-ready');
    this.style.removeProperty('--slideshow-height');
  }

  measureSlideHeight(slide) {
    const slideHeight = Math.max(slide.scrollHeight, slide.offsetHeight);
    const content = slide.querySelector('.slideshow__content');
    const contentHeight = content ? content.scrollHeight : 0;
    const split = slide.querySelector('.hero-container');
    const splitHeight = split ? split.scrollHeight : 0;
    return Math.max(slideHeight, contentHeight, splitHeight);
  }

  equalizeSlideHeights() {
    if (!this.isAutoHeight() || this._equalizingHeight) return;

    this._equalizingHeight = true;
    this.classList.remove('slideshow--auto-ready');
    this.style.removeProperty('--slideshow-height');
    void this.offsetHeight;

    const originals = this.getOriginalSlides();
    let maxHeight = 0;
    originals.forEach((slide) => {
      maxHeight = Math.max(maxHeight, this.measureSlideHeight(slide));
    });

    this._autoHeightWidth = Math.round(this.getBoundingClientRect().width);

    if (maxHeight > 0) {
      this.style.setProperty('--slideshow-height', `${Math.round(maxHeight)}px`);
      this.classList.add('slideshow--auto-ready');
      this.swiper?.update();
    }

    this._equalizingHeight = false;
  }

  bindAutoplayButton(autoplayBtn, autoplayEnabled) {
    if (!autoplayBtn || !autoplayEnabled || !this.swiper) return;
    if (autoplayBtn.dataset.bound === 'true') return;
    autoplayBtn.dataset.bound = 'true';

    autoplayBtn.classList.remove('slideshow__autoplay--paused');
    autoplayBtn.setAttribute(
      'aria-label',
      window.accessibilityStrings?.pauseSlideshow || 'Pause slideshow'
    );

    autoplayBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!this.swiper?.autoplay) return;

      if (this.swiper.autoplay.running) {
        this.swiper.autoplay.stop();
        autoplayBtn.classList.add('slideshow__autoplay--paused');
        autoplayBtn.style.setProperty('--autoplay-progress', '0%');
        autoplayBtn.setAttribute(
          'aria-label',
          window.accessibilityStrings?.playSlideshow || 'Play slideshow'
        );
      } else {
        this.swiper.autoplay.start();
        autoplayBtn.classList.remove('slideshow__autoplay--paused');
        autoplayBtn.setAttribute(
          'aria-label',
          window.accessibilityStrings?.pauseSlideshow || 'Pause slideshow'
        );
      }
    });
  }

  updateSlideA11y(swiper) {
    swiper.slides.forEach((slide) => {
      const isActive = slide.classList.contains('swiper-slide-active');
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (!isActive) {
        slide.setAttribute('tabindex', '-1');
      } else {
        slide.removeAttribute('tabindex');
      }
      slide.querySelectorAll('a, button').forEach((el) => {
        if (isActive) {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', '-1');
        }
      });
    });
  }
}

if (!customElements.get('home-slider-component')) {
  customElements.define('home-slider-component', HomeSliderComponent);
}

document.addEventListener('shopify:section:unload', (event) => {
  const component = event.target.querySelector('home-slider-component');
  if (component && typeof component.destroySwiper === 'function') {
    component.destroySwiper();
  }
});

document.addEventListener('shopify:section:load', (event) => {
  const component = event.target.querySelector('home-slider-component');
  if (component && typeof component.initWhenReady === 'function') {
    component.initWhenReady();
  }
});

document.addEventListener('shopify:block:select', (event) => {
  const component = event.target.closest('home-slider-component');
  if (component && typeof component.selectSlide === 'function') {
    component.selectSlide(event.target, event.detail);
  }
});

document.addEventListener('shopify:block:deselect', (event) => {
  const component = event.target.closest('home-slider-component');
  if (component && typeof component.deselectSlide === 'function') {
    component.deselectSlide();
  }
});
